import React from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faEnvelopeOpenText,
  faCheckCircle,
  faKey,
  faSignInAlt,
  faExclamationTriangle,
  faRedo,
  faUserCheck,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "../../../../i18n/LanguageContext";
import "./css/NewMemberInfoModal.css";

/**
 * NewMemberInfoModal
 * Shown to the account owner immediately after a new member user is created.
 * Summarises exactly what the new member will receive and what they need to do
 * so the owner can answer questions confidently.
 */
function NewMemberInfoModal({ isOpen, onClose, memberName, memberEmail }) {
  const { t } = useTranslation();
  const modalRef = React.useRef(null);

  // Scroll to top each time the modal opens
  React.useEffect(() => {
    if (isOpen && modalRef.current) {
      modalRef.current.scrollTop = 0;
    }
  }, [isOpen]);

  // Close on Escape
  React.useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  // Lock body scroll while open
  React.useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const steps = [
    {
      icon: faEnvelopeOpenText,
      color: "blue",
      title: t("usermgmt.newMember.step1.title"),
      body: (
        <>
          {t("usermgmt.newMember.step1.body.sentTo")}{" "}
          <strong className="nmi-email">{memberEmail}</strong>
          {t("usermgmt.newMember.step1.body.checkInbox")}{" "}
          <strong>{t("usermgmt.newMember.step1.body.inbox")}</strong>{" "}
          {t("usermgmt.newMember.step1.body.alsoCheck")}{" "}
          <strong>{t("usermgmt.newMember.step1.body.spamJunk")}</strong>{" "}
          {t("usermgmt.newMember.step1.body.suffix")}
        </>
      ),
    },
    {
      icon: faCheckCircle,
      color: "green",
      title: t("usermgmt.newMember.step2.title"),
      body: t("usermgmt.newMember.step2.body"),
    },
    {
      icon: faKey,
      color: "amber",
      title: t("usermgmt.newMember.step3.title"),
      body: t("usermgmt.newMember.step3.body"),
    },
    {
      icon: faSignInAlt,
      color: "purple",
      title: t("usermgmt.newMember.step4.title"),
      body: t("usermgmt.newMember.step4.body"),
    },
  ];

  const modal = (
    <div
      className="nmi-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="nmi-title"
    >
      <div className="nmi-modal" ref={modalRef}>
        {/* ── Header ── */}
        <div className="nmi-header">
          <div className="nmi-header-left">
            <span className="nmi-header-icon">
              <FontAwesomeIcon icon={faUserCheck} />
            </span>
            <div>
              <h2 id="nmi-title" className="nmi-title">
                {t("usermgmt.newMember.title")}
              </h2>
              {memberName && (
                <p className="nmi-subtitle">
                  {t("usermgmt.newMember.subtitle")}{" "}
                  <strong>{memberName}</strong>
                </p>
              )}
            </div>
          </div>
          <button
            className="nmi-close"
            onClick={onClose}
            aria-label={t("usermgmt.newMember.closeAriaLabel")}
            type="button"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* ── Intro ── */}
        <div className="nmi-body">
          <p className="nmi-intro">{t("usermgmt.newMember.intro")}</p>

          {/* ── Steps ── */}
          <ol
            className="nmi-steps"
            aria-label={t("usermgmt.newMember.stepsAriaLabel")}
          >
            {steps.map((step, i) => (
              <li key={i} className={`nmi-step nmi-step--${step.color}`}>
                <div className={`nmi-step-icon nmi-step-icon--${step.color}`}>
                  <FontAwesomeIcon icon={step.icon} />
                </div>
                <div className="nmi-step-content">
                  <p className="nmi-step-title">
                    <span className="nmi-step-num">{i + 1}.</span> {step.title}
                  </p>
                  <p className="nmi-step-body">{step.body}</p>
                </div>
              </li>
            ))}
          </ol>

          {/* ── Tip: what if no email ── */}
          <div className="nmi-tip">
            <span className="nmi-tip-icon">
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </span>
            <div>
              <p className="nmi-tip-title">
                {t("usermgmt.newMember.tip.title")}
              </p>
              <p className="nmi-tip-body">
                {t("usermgmt.newMember.tip.body.goto")}{" "}
                <strong>{t("usermgmt.newMember.tip.body.userList")}</strong>
                {t("usermgmt.newMember.tip.body.findMember")}{" "}
                <em>{t("usermgmt.newMember.tip.body.unverified")}</em>
                {t("usermgmt.newMember.tip.body.openMenu")}{" "}
                <strong>{t("usermgmt.newMember.tip.body.resendAction")}</strong>
                .{" "}
                <FontAwesomeIcon
                  icon={faRedo}
                  className="nmi-tip-inline-icon"
                />{" "}
                {t("usermgmt.newMember.tip.body.howMany")}
              </p>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="nmi-footer">
          <button
            type="button"
            className="nmi-btn-primary"
            onClick={onClose}
            autoFocus
          >
            {t("usermgmt.newMember.gotIt")}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

export default NewMemberInfoModal;
